import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

import { MetaInterest } from "@/types";

interface MetaRecipeState {
  selection: MetaInterest[][];
  setSelection: (recipe: MetaInterest[][]) => void;
  activeSelection: number;
  setActiveSelection: (id: number) => void;
  addSelection: () => void;
  removeSelection: (idx: number) => void;
  selectInterest: (interest: MetaInterest, listId: number) => void;
  selectMultiInterest: (interests: MetaInterest[], listId: number) => void;
  removeInterest: (interest: MetaInterest, listId: number) => void;
}


// Toggles an interest in a list
const updateSelection = (interest: MetaInterest, interests: MetaInterest[]) => {
  if (interests.map(x => x.meta_id).includes(interest.meta_id)) {
    return interests.filter(x => x.meta_id !== interest.meta_id)
  } else {
    return [...interests, interest];
  }
}

const MetaRecipeContext = createContext<MetaRecipeState | undefined>(undefined);

export const MetaRecipeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selection, setSelection] = useState<MetaInterest[][]>([]);
  const [activeSelection, setActiveSelection] = useState<number>(0);

  const addSelection = useCallback(() => {
    setSelection(prev => [...prev, []]);
    setActiveSelection(prev => prev + 1);
  }, []);

  const removeSelection = useCallback((idx: number) => {
    setSelection(prev => prev.filter((_, i) => i !== idx));
    setActiveSelection(prev => prev - 1);
  }, []);

  const selectInterest = useCallback((interest: MetaInterest, listId: number) => {
    setSelection(prev => {
      const target = prev[listId];
      const update = updateSelection(interest, target);
      return prev.map((list, i) => (i === listId ? update : list));
    })
  }, []);

  const selectMultiInterest = useCallback((interests: MetaInterest[], listId: number) => {
    setSelection(prev => {
      const existing = prev[listId];
      const itemIds = new Set(existing.map(x => x.meta_id));
      const newItems = interests.filter(x => !itemIds.has(x.meta_id));
      const update = [...existing, ...newItems];

      const next = prev.slice();
      next[listId] = update;
      return next;
    })
  }, []);

  const removeInterest = useCallback((interest: MetaInterest, listId: number) => {
    setSelection(prev => {
      const update = prev[listId].filter(x => x.meta_id !== interest.meta_id);
      return prev.map((list, i) => (i === listId ? update : list))
    })
  }, []);

  const value = useMemo<MetaRecipeState>(() => ({
    selection,
    setSelection,
    activeSelection,
    setActiveSelection,
    addSelection,
    removeSelection,
    selectInterest,
    selectMultiInterest,
    removeInterest,
  }), [selection, activeSelection, addSelection, removeSelection, selectInterest, selectMultiInterest, removeInterest])

  return (
    <MetaRecipeContext.Provider value={value}>
      {children}
    </MetaRecipeContext.Provider>
  );
};

export const useMetaRecipe = () => {
  const context = useContext(MetaRecipeContext);
  if (!context) {
    throw new Error(
      "useMetaRecipe must be used within a MetaRecipeProvider",
    );
  }
  return context;
};

